import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Share2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { GraphFindingsForceGraph } from '../components/GraphFindingsForceGraph';
import MagicBento from '../components/MagicBento';
import { GRAPH_BENTO_CARDS, GRAPH_BENTO_GLOW } from '../data/magicBentoPresets';

const SEVERITIES = ['', 'critical', 'high', 'medium', 'low', 'info', 'unknown'];

function severityVariant(sev) {
  const s = (sev || '').toLowerCase();
  if (s === 'critical') return 'destructive';
  if (s === 'high') return 'destructive';
  if (s === 'medium') return 'default';
  return 'secondary';
}

export function GraphFindingsPage() {
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState({ items: [], total: 0, configured: false });
  const [viz, setViz] = useState({ configured: false, nodes: [], links: [], truncated: false });
  const [severity, setSeverity] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [sum, data, v] = await Promise.all([
        api.get('/api/graph/findings/summary'),
        api.get(
          `/api/graph/findings?limit=100&offset=0${severity ? `&severity=${encodeURIComponent(severity)}` : ''}`
        ),
        api.get('/api/graph/visualization?limit_nodes=500&limit_rels=2000'),
      ]);
      setSummary(sum);
      setList(data);
      setViz(v);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [severity]);

  const onNodeClick = useCallback((node) => {
    setSelectedNode(node);
  }, []);

  const bySev = summary?.by_severity || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Share2 className="mt-1 h-10 w-10 text-primary" />
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">Graph findings</h1>
            <p className="max-w-3xl text-muted-foreground">
              Findings and related nodes synced from the autonomous pipeline into Neo4j. Explore the live graph and
              the full property table below — no separate database console required.
            </p>
          </div>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => load()} disabled={loading}>
          Refresh all
        </Button>
      </div>

      <section className="space-y-3" aria-labelledby="graph-bento-heading">
        <h2
          id="graph-bento-heading"
          className="font-heading text-lg font-semibold tracking-tight text-foreground"
        >
          What the graph shows
        </h2>
        <MagicBento
          cards={GRAPH_BENTO_CARDS}
          glowColor={GRAPH_BENTO_GLOW}
          enableSpotlight={false}
          particleCount={8}
        />
      </section>

      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
          <CardDescription>
            {summary?.configured === false && 'Neo4j is not configured on the API (set NEO4J_URI).'}
            {summary?.auth_failed && ' Neo4j authentication failed — verify URI/user/password env vars.'}
            {summary?.error && <span className="text-amber-600">{summary.error}</span>}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {error && <p className="text-sm text-red-500">{error}</p>}
          {!loading && summary?.configured && (
            <div className="flex flex-wrap gap-4">
              <div className="rounded-lg border border-border bg-card px-4 py-3">
                <div className="text-xs text-muted-foreground">Total findings (summary)</div>
                <div className="font-mono text-2xl font-semibold">{summary.total ?? 0}</div>
              </div>
              {Object.keys(bySev).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(bySev).map(([k, v]) => (
                    <Badge key={k} variant="outline">
                      {k}: {v}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Interactive graph</CardTitle>
          <CardDescription>
            Each dot is a Neo4j node: read the title under it, use the legend for colors, hover for details, and click to
            inspect properties below. Pan (drag background), zoom (scroll), drag nodes to untangle. Synced from{' '}
            <code className="rounded bg-secondary px-1">GET /api/graph/visualization</code>
            {viz.truncated && (
              <span className="text-amber-600">
                {' '}
                — large graphs are truncated; increase limits in the API if needed.
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!viz.configured && !loading && (
            <p className="text-sm text-muted-foreground">
              Connect Neo4j to see the graph.
              {viz?.auth_failed ? (
                <span className="ml-1 text-amber-600">
                  Authentication failed — check `NEO4J_URI`, `NEO4J_USER`, and `NEO4J_PASSWORD`.
                </span>
              ) : null}
            </p>
          )}
          {viz.configured && (viz.nodes?.length ?? 0) === 0 && !loading && (
            <p className="text-sm text-muted-foreground">
              No nodes in the graph store yet. Run a pipeline demo or engagement that syncs findings.
            </p>
          )}
          {viz.configured && viz.nodes?.length > 0 && (
            <>
              <GraphFindingsForceGraph graphData={viz} onNodeClick={onNodeClick} highlightId={selectedNode?.id} />
              {selectedNode && (
                <div className="rounded-lg border border-border bg-secondary/30 p-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium">Selected node</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedNode(null)}>
                      Clear
                    </Button>
                  </div>
                  <pre className="max-h-64 overflow-auto text-xs text-muted-foreground">
                    {JSON.stringify(
                      {
                        id: selectedNode.id,
                        labels: selectedNode.labels,
                        group: selectedNode.group,
                        properties: selectedNode.properties,
                      },
                      null,
                      2
                    )}
                  </pre>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Findings table</CardTitle>
          <CardDescription>Same :Finding rows as in Neo4j; filter by severity.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-row flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              {list.configured === false
                ? 'Connect Neo4j to see rows.'
                : `${list.total ?? 0} total (showing ${list.items?.length ?? 0})`}
            </p>
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground">Filter severity</label>
              <select
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
              >
                {SEVERITIES.map((s) => (
                  <option key={s || 'all'} value={s}>
                    {s || 'All'}
                  </option>
                ))}
              </select>
              <Button type="button" variant="outline" size="sm" onClick={() => load()}>
                Refresh
              </Button>
            </div>
          </div>
          {!list.configured && !loading && (
            <p className="text-sm text-muted-foreground">Graph store not connected.</p>
          )}
          {list.configured && (list.items?.length ?? 0) === 0 && !loading && (
            <p className="text-sm text-muted-foreground">
              No Finding nodes yet. Run <strong>Run demo (synthetic)</strong> on AERTA+ pipeline, or an engagement with
              CVE/misconfig rows in the Layer 1 report.
            </p>
          )}
          {list.items && list.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                    <th className="py-2 pr-2">Severity</th>
                    <th className="py-2 pr-2">Title</th>
                    <th className="py-2 pr-2">Phase</th>
                    <th className="py-2 pr-2">Mode</th>
                    <th className="py-2">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {list.items.map((row) => (
                    <tr key={row.id} className="border-b border-border/60">
                      <td className="py-2 pr-2">
                        <Badge variant={severityVariant(row.severity)}>{row.severity}</Badge>
                      </td>
                      <td className="max-w-md py-2 pr-2 font-medium">
                        <Link
                          className="text-primary underline hover:no-underline"
                          to={`/graph-findings/finding/${encodeURIComponent(row.id)}`}
                        >
                          {row.title}
                        </Link>
                      </td>
                      <td className="py-2 pr-2 font-mono text-xs text-muted-foreground">{row.phase || '—'}</td>
                      <td className="py-2 pr-2 text-xs">{row.mode || '—'}</td>
                      <td className="py-2 text-xs text-muted-foreground">{row.updated_at || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        <Link to="/autonomous-pipeline" className="text-primary underline">
          ← Back to AERTA+ pipeline
        </Link>
      </p>
    </div>
  );
}
